/* ============================================
   ABOUT PAGE
   The Creative Orchestra - GEMINI Framework
   About the author and research
   ============================================ */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, 
  Mail, 
  Linkedin, 
  Globe,
  GraduationCap,
  Coffee,
  Moon,
  Sparkles,
  Quote,
  BookOpen,
  ArrowRight,
  ExternalLink,
  Headphones,
  Play,
  Pause,
  Volume2,
} from 'lucide-react';
import { ScrollReveal } from '@components/common';
import { profile } from '@data';
import { SITE_INFO } from '@utils/constants';

/**
 * About Page Component
 * Displays author profile, research info, and philosophy
 */
const AboutPage = () => {
  // Set page title
  useEffect(() => {
    document.title = `About ${profile.name} | ${SITE_INFO.title}`;
  }, []);

  return (
    <div className="min-h-screen py-20">
      <div className="max-w-4xl mx-auto px-4">
        {/* Profile Header */}
        <ProfileHeader />

        {/* Bio Section */}
        <BioSection />

        {/* Audio Self-Introduction */}
        <AudioIntroSection />

        {/* Thesis Section */}
        <ThesisSection />

        {/* Research Interests */}
        <ResearchInterests />

        {/* Skills */}
        <SkillsSection />

        {/* Philosophy */}
        <PhilosophySection />

        {/* Fun Facts */}
        <FunFacts />

        {/* Contact */}
        <ContactSection />
      </div>
    </div>
  );
};

/**
 * Profile Header
 */
const ProfileHeader = () => {
  return (
    <ScrollReveal>
      <header className="text-center mb-16">
        {/* Profile photo */}
        <motion.div
          className="relative w-64 h-64 mx-auto mb-6"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.6 }}
        >
          <div className="w-full h-full rounded-full overflow-hidden border-4 border-white/10
            bg-gradient-to-br from-indigo-500/20 to-purple-500/20">
            <img
              src={profile.photo}
              alt={profile.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `
                  <div class="w-full h-full flex items-center justify-center text-4xl font-bold text-white">
                    ${profile.name.charAt(0)}
                  </div>
                `;
              }}
            />
          </div>
          {/* Decorative ring */}
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-indigo-500/30"
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          />
        </motion.div>

        {/* Name */}
        <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-2">
          {profile.name}
        </h1>

        {/* Title */}
        <p className="text-xl text-indigo-400 mb-4">
          {profile.title}
        </p>

        {/* Institution & Location */}
        <div className="flex flex-wrap items-center justify-center gap-4 text-gray-400">
          <div className="flex items-center gap-2">
            <GraduationCap className="w-4 h-4" />
            <span>{profile.institution}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span>{profile.location}</span>
          </div>
        </div>

        {/* Tagline */}
        <p className="text-gray-500 mt-4 italic">
          "{profile.tagline}"
        </p>
      </header>
    </ScrollReveal>
  );
};

/**
 * Bio Section
 */
const BioSection = () => {
  return (
    <ScrollReveal delay={0.1}>
      <section className="mb-16">
        <h2 className="text-2xl font-display font-semibold text-white mb-6">
          About Me
        </h2>
        <div className="space-y-4 text-gray-400 leading-relaxed">
          {profile.bio.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
      </section>
    </ScrollReveal>
  );
};

/**
 * Sound Bar - single animated bar
 */
const SoundBar = ({ index, isPlaying, barCount }) => {
  const height = Math.random() * 24 + 8;
  return (
    <motion.div
      className="w-[3px] rounded-full origin-bottom"
      style={{
        background: `linear-gradient(to top, #818CF8, #A78BFA, #C084FC)`,
      }}
      animate={
        isPlaying
          ? {
              scaleY: [
                0.3 + Math.random() * 0.2,
                0.8 + Math.random() * 0.2,
                0.4 + Math.random() * 0.3,
                1,
                0.5 + Math.random() * 0.2,
                0.3 + Math.random() * 0.2,
              ],
              height: [
                `${height * 0.4}px`,
                `${height * 1.2}px`,
                `${height * 0.6}px`,
                `${height}px`,
                `${height * 0.8}px`,
                `${height * 0.4}px`,
              ],
            }
          : { scaleY: 0.3, height: '6px' }
      }
      transition={
        isPlaying
          ? {
              duration: 0.8 + Math.random() * 0.5,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: index * 0.05,
            }
          : { duration: 0.4, ease: 'easeOut' }
      }
    />
  );
};

/**
 * Audio Intro Section - Self-description audio player
 */
const AudioIntroSection = () => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const barCount = 40;

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleTimeUpdate = useCallback(() => {
    if (!audioRef.current) return;
    const current = audioRef.current.currentTime;
    const dur = audioRef.current.duration;
    setCurrentTime(current);
    setProgress(dur ? (current / dur) * 100 : 0);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
  }, []);

  const handleSeek = useCallback((e) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    audioRef.current.currentTime = percentage * audioRef.current.duration;
  }, []);

  return (
    <ScrollReveal delay={0.15}>
      <section className="mb-16">
        <motion.div
          className="relative overflow-hidden rounded-2xl border border-white/10
            bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          whileHover={{ borderColor: 'rgba(129, 140, 248, 0.3)' }}
          transition={{ duration: 0.5 }}
        >
          {/* Glowing background effect when playing */}
          <AnimatePresence>
            {isPlaying && (
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 via-purple-500/10 to-indigo-500/5"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              />
            )}
          </AnimatePresence>

          <div className="relative p-6 md:p-8">
            {/* Header with icon */}
            <div className="flex items-center gap-3 mb-4">
              <motion.div
                className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center"
                animate={isPlaying ? { scale: [1, 1.1, 1] } : {}}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <Headphones className="w-5 h-5 text-indigo-400" />
              </motion.div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Hear It From Me
                </h3>
                <p className="text-sm text-gray-500">
                  A quick audio intro — because reading bios is so last century
                </p>
              </div>
            </div>

            {/* Caption */}
            <p className="text-gray-400 text-sm mb-6 pl-[52px]">
              <span className="text-indigo-400">🎧</span>{' '}
              Pop in your earbuds and let me tell you who I am, what drives my research,
              and why I chose this wild ride called a PhD. It's like a trailer — but for a researcher.
            </p>

            {/* Sound bars visualizer */}
            <div className="flex items-end justify-center gap-[3px] h-10 mb-6 px-4">
              {Array.from({ length: barCount }).map((_, i) => (
                <SoundBar key={i} index={i} isPlaying={isPlaying} barCount={barCount} />
              ))}
            </div>

            {/* Player controls */}
            <div className="flex items-center gap-4 px-2">
              {/* Play/Pause button */}
              <motion.button
                onClick={togglePlay}
                className="w-12 h-12 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500
                  flex items-center justify-center text-white shadow-lg shadow-indigo-500/25
                  hover:shadow-indigo-500/40 transition-shadow flex-shrink-0"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5 ml-0.5" />
                )}
              </motion.button>

              {/* Progress bar */}
              <div className="flex-1 flex flex-col gap-1">
                <div
                  className="w-full h-1.5 bg-white/10 rounded-full cursor-pointer group relative"
                  onClick={handleSeek}
                >
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 relative"
                    style={{ width: `${progress}%` }}
                    transition={{ duration: 0.1 }}
                  >
                    <motion.div
                      className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full
                        bg-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                  </motion.div>
                </div>
                <div className="flex justify-between text-[11px] text-gray-600">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Volume icon */}
              <Volume2 className="w-4 h-4 text-gray-600 flex-shrink-0" />
            </div>
          </div>

          {/* Hidden audio element */}
          <audio
            ref={audioRef}
            src="/assets/audio/ShortSelfBioAudio.m4a"
            preload="metadata"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={handleEnded}
          />
        </motion.div>
      </section>
    </ScrollReveal>
  );
};

/**
 * Thesis Section
 */
const ThesisSection = () => {
  return (
    <ScrollReveal delay={0.2}>
      <section className="mb-16">
        <div className="p-6 md:p-8 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-purple-500/5
          border border-white/10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center
              flex-shrink-0">
              <BookOpen className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-sm text-gray-500 mb-1">PhD Thesis</h3>
              <h2 className="text-xl font-semibold text-white mb-2">
                {profile.thesis.title}
              </h2>
              <p className="text-indigo-400 font-medium mb-3">
                {profile.thesis.frameworkFull}
              </p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                <span>Advisor: {profile.thesis.advisor}</span>
                <span>Year: {profile.thesis.year}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
};

/**
 * Research Interests
 */
const ResearchInterests = () => {
  return (
    <ScrollReveal delay={0.3}>
      <section className="mb-16">
        <h2 className="text-2xl font-display font-semibold text-white mb-6">
          Research Interests
        </h2>
        <div className="flex flex-wrap gap-3">
          {profile.researchInterests.map((interest, index) => (
            <motion.span
              key={index}
              className="px-4 py-2 rounded-full bg-white/5 border border-white/10
                text-gray-300 text-sm"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ 
                scale: 1.05,
                backgroundColor: 'rgba(129, 140, 248, 0.1)',
                borderColor: 'rgba(129, 140, 248, 0.3)',
              }}
            >
              {interest}
            </motion.span>
          ))}
        </div>
      </section>
    </ScrollReveal>
  );
};

/**
 * Skills Section
 */
const SkillsSection = () => {
  const skillCategories = [
    { title: 'Programming', items: profile.skills.programming, icon: '💻' },
    { title: 'Tools', items: profile.skills.tools, icon: '🛠️' },
    { title: 'AI/ML', items: profile.skills.ai, icon: '🤖' },
    { title: 'Design', items: profile.skills.design, icon: '🎨' },
  ];

  return (
    <ScrollReveal delay={0.4}>
      <section className="mb-16">
        <h2 className="text-2xl font-display font-semibold text-white mb-6">
          Skills & Tools
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {skillCategories.map((category, catIndex) => (
            <motion.div
              key={category.title}
              className="p-4 rounded-xl bg-white/5 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: catIndex * 0.1 }}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">{category.icon}</span>
                <h3 className="font-medium text-white">{category.title}</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {category.items.map((skill, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 text-xs rounded bg-white/5 text-gray-400"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </ScrollReveal>
  );
};

/**
 * Philosophy Section
 */
const PhilosophySection = () => {
  return (
    <ScrollReveal delay={0.5}>
      <section className="mb-16">
        <h2 className="text-2xl font-display font-semibold text-white mb-6">
          Philosophy
        </h2>
        
        {/* Quote */}
        <div className="relative p-6 rounded-xl bg-white/5 border border-white/10 mb-6">
          <Quote className="absolute top-4 left-4 w-8 h-8 text-indigo-500/30" />
          <blockquote className="text-lg text-gray-300 italic pl-8">
            {profile.philosophy.quote}
          </blockquote>
        </div>

        {/* Principles */}
        <div className="space-y-3">
          {profile.philosophy.principles.map((principle, index) => (
            <motion.div
              key={index}
              className="flex items-start gap-3"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Sparkles className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
              <p className="text-gray-400">{principle}</p>
            </motion.div>
          ))}
        </div>

        {/* Mentor */}
        <div className="mt-6 p-4 rounded-xl bg-indigo-500/5 border border-indigo-500/20">
          <p className="text-sm text-gray-500 mb-1">Wisdom from my mentor</p>
          <p className="text-indigo-400 font-medium">
            {profile.mentor.name}: "{profile.mentor.wisdom}"
          </p>
        </div>
      </section>
    </ScrollReveal>
  );
};

/**
 * Fun Facts
 */
const FunFacts = () => {
  const factIcons = [Moon, Coffee, Sparkles, Moon];

  return (
    <ScrollReveal delay={0.6}>
      <section className="mb-16">
        <h2 className="text-2xl font-display font-semibold text-white mb-6">
          Fun Facts
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {profile.funFacts.map((fact, index) => {
            const Icon = factIcons[index] || Sparkles;
            return (
              <motion.div
                key={index}
                className="p-4 rounded-xl bg-white/5 border border-white/10 text-center"
                whileHover={{ y: -4, borderColor: 'rgba(129, 140, 248, 0.3)' }}
              >
                <Icon className="w-6 h-6 text-indigo-400 mx-auto mb-2" />
                <p className="text-sm text-gray-400">{fact}</p>
              </motion.div>
            );
          })}
        </div>
      </section>
    </ScrollReveal>
  );
};

/**
 * Contact Section
 */
const ContactSection = () => {
  const socialLinks = [
    { 
      icon: Linkedin, 
      label: 'LinkedIn', 
      url: profile.social.linkedin,
      color: '#0A66C2',
    },
    { 
      icon: Globe, 
      label: 'Website', 
      url: profile.social.website,
      color: '#818CF8',
    },
    { 
      icon: Mail, 
      label: 'Email', 
      url: `mailto:${profile.social.email}`,
      color: '#EA4335',
    },
  ];

  return (
    <ScrollReveal delay={0.7}>
      <section className="text-center">
        <h2 className="text-2xl font-display font-semibold text-white mb-6">
          Get in Touch
        </h2>
        
        <div className="flex justify-center gap-4 mb-8">
          {socialLinks.map((link) => (
            <motion.a
              key={link.label}
              href={link.url}
              target={link.url.startsWith('mailto') ? undefined : '_blank'}
              rel={link.url.startsWith('mailto') ? undefined : 'noopener noreferrer'}
              className="flex items-center gap-2 px-4 py-2 rounded-lg
                bg-white/5 border border-white/10 text-gray-400
                hover:text-white hover:border-white/20 transition-all"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <link.icon className="w-5 h-5" />
              <span>{link.label}</span>
              {!link.url.startsWith('mailto') && (
                <ExternalLink className="w-3 h-3 opacity-50" />
              )}
            </motion.a>
          ))}
        </div>

        <Link to="/novel">
          <motion.button
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full
              bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium"
            whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(129, 140, 248, 0.5)' }}
            whileTap={{ scale: 0.95 }}
          >
            <BookOpen className="w-5 h-5" />
            Read My Story
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        </Link>
      </section>
    </ScrollReveal>
  );
};

export default AboutPage;
